import { matchPath } from 'react-router-dom';

import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { isShellObjectIdParam } from '@/core/router/shell-object-id-param';

export const LOCATION_DETAIL_PATH = `${shellPaths.moreWorkspaceLocations}/:id`;

function idFromPathname(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;
  const matched = matchPath(
    { path: LOCATION_DETAIL_PATH, end: true },
    canonicalizeShellPathname(pathname),
  );
  const id = matched?.params?.id;
  return isShellObjectIdParam(id) ? id : undefined;
}

/**
 * Ionic can leave `useParams` empty or stale on in-stack pushes.
 * Prefer an id from any current pathname that matches the detail route.
 */
export function resolveLocationId(
  params: { id?: string },
  ...pathnames: Array<string | undefined>
): string | undefined {
  for (const pathname of pathnames) {
    const fromPath = idFromPathname(pathname);
    if (fromPath) return fromPath;
  }
  return isShellObjectIdParam(params.id) ? params.id : undefined;
}

/**
 * The URL is a location detail whose id can never load ("abc"), not the
 * static /locations/create sibling. Without this the page waited forever for
 * an id (DEV-558).
 */
export function isMalformedLocationDetailPath(pathname: string): boolean {
  const canonical = canonicalizeShellPathname(pathname);
  if (canonical === shellPaths.moreWorkspaceLocationCreate) return false;
  const matched = matchPath(
    { path: LOCATION_DETAIL_PATH, end: true },
    canonical,
  );
  const id = matched?.params?.id;
  return !!id && !isShellObjectIdParam(id);
}
